/**
 * BhiSnapshotSaveCard.jsx — iter740 (Feb 2026)
 *
 * "Запази BHI snapshot" бутон + компактна история на последните snapshot-и.
 * Използва вече съществуващите endpoint-и:
 *   POST /api/breast-intelligence/snapshots/save     — записва текущия snapshot
 *   GET  /api/breast-intelligence/snapshots          — списък (последни 10)
 *   DELETE /api/breast-intelligence/snapshots/{id}   — изтрий
 *
 * Пази последните 10 (backend enforced). Дава на пациента и клинициста
 * контрола да замрази точката във времето (напр. „Pre-supplement baseline
 * — 12.02.2026") и да проследи прогресията.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Camera, Clock, Loader2, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

export default function BhiSnapshotSaveCard({ isDark = false }) {
  const { API, user } = useAuth();
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/breast-intelligence/snapshots`);
      setItems(Array.isArray(data?.snapshots) ? data.snapshots : (Array.isArray(data) ? data : []));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [API, user]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/breast-intelligence/snapshots/save`, { label: label.trim() });
      toast.success("Snapshot записан ✓");
      setLabel("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при записа");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/breast-intelligence/snapshots/${id}`);
      setItems((prev) => prev.filter((s) => (s.id || s._id) !== id));
      toast.success("Изтрит");
    } catch {
      toast.error("Не успя");
    }
  };

  if (!user) return null;

  return (
    <section
      className={`rounded-2xl border p-5 ${isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}
      data-testid="bhi-snapshot-save"
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            📸 Запази моментна снимка на BHI
          </h3>
          <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            Замрази текущото състояние — за сравнение по-късно или споделяне с лекар. Пази последните 10.
          </p>
        </div>
      </header>

      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder='Опционален етикет (напр. „Преди мамография — февр. 2026")'
          maxLength={80}
          data-testid="bhi-snapshot-label-input"
          className={`flex-1 rounded-lg border px-3 py-2 text-sm outline-none transition ${
            isDark
              ? "border-white/10 bg-slate-900/40 text-white placeholder-slate-500 focus:border-pink-500/40"
              : "border-slate-200 bg-white text-slate-800 placeholder-slate-400 focus:border-pink-500"
          }`}
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="bhi-snapshot-save-btn"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:from-pink-600 hover:to-purple-600 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          Запази snapshot
        </button>
      </div>

      {/* History */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`inline-flex items-center gap-1.5 text-xs font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}
          data-testid="bhi-snapshot-history-toggle"
        >
          <Clock className="h-3.5 w-3.5" />
          История ({items.length}) — {expanded ? "скрий" : "покажи"}
        </button>

        {expanded && (
          <div className="mt-2 space-y-1.5" data-testid="bhi-snapshot-history">
            {loading ? (
              <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Зареждане...</div>
            ) : items.length === 0 ? (
              <div className={`rounded-md border border-dashed px-3 py-2 text-xs ${isDark ? "border-white/10 text-slate-400" : "border-slate-300 text-slate-500"}`}>
                Все още нямате запазени snapshot-и.
              </div>
            ) : (
              items.map((s) => {
                const id = s.id || s._id;
                const createdAt = s.created_at ? new Date(s.created_at).toLocaleString("bg-BG", { dateStyle: "short", timeStyle: "short" }) : "";
                return (
                  <div
                    key={id}
                    className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-xs ${isDark ? "border-white/10 bg-slate-900/40" : "border-slate-200 bg-slate-50"}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium truncate ${isDark ? "text-white" : "text-slate-800"}`}>
                        <CheckCircle2 className="mr-1 inline h-3 w-3 text-emerald-500" />
                        {s.label || createdAt}
                      </div>
                      <div className={isDark ? "text-slate-500" : "text-slate-400"}>{createdAt}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => del(id)}
                      className="inline-flex items-center rounded-md p-1 text-slate-400 hover:text-red-500"
                      title="Изтрий"
                      data-testid={`bhi-snapshot-delete-${id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </section>
  );
}
